import type { CSSProperties } from 'react';
import { BRAND_ASSETS } from '../../lib/brand';

type BrandLogoVariant = 'navbar' | 'auth' | 'marketing' | 'icon';
type BrandLogoTone = 'light' | 'dark';

interface BrandLogoProps {
  variant?: BrandLogoVariant;
  tone?: BrandLogoTone;
  alt?: string;
  style?: CSSProperties;
  responsive?: boolean;
}

function getHeights(variant: BrandLogoVariant): { full: number; icon: number } {
  switch (variant) {
    case 'auth':
      return { full: 72, icon: 48 };
    case 'marketing':
      return { full: 62, icon: 42 };
    case 'icon':
      return { full: 34, icon: 34 };
    case 'navbar':
    default:
      return { full: 44, icon: 34 };
  }
}

function getSources(variant: BrandLogoVariant, tone: BrandLogoTone) {
  if (variant === 'icon') {
    return {
      full: tone === 'light' ? BRAND_ASSETS.iconLight : BRAND_ASSETS.iconDark,
      icon: tone === 'light' ? BRAND_ASSETS.iconLight : BRAND_ASSETS.iconDark,
    };
  }

  if (variant === 'navbar') {
    return {
      full:
        tone === 'light'
          ? BRAND_ASSETS.horizontalLight
          : BRAND_ASSETS.horizontalDark,
      icon: tone === 'light' ? BRAND_ASSETS.iconLight : BRAND_ASSETS.iconDark,
    };
  }

  if (variant === 'auth') {
    return {
      full: tone === 'light' ? BRAND_ASSETS.authFullLight : BRAND_ASSETS.fullDark,
      icon: tone === 'light' ? BRAND_ASSETS.iconLight : BRAND_ASSETS.iconDark,
    };
  }

  return {
    full: tone === 'light' ? BRAND_ASSETS.fullLight : BRAND_ASSETS.fullDark,
    icon: tone === 'light' ? BRAND_ASSETS.iconLight : BRAND_ASSETS.iconDark,
  };
}

export default function BrandLogo({
  variant = 'navbar',
  tone = variant === 'navbar' ? 'light' : 'dark',
  alt = 'AbleComply',
  style,
  responsive = false,
}: BrandLogoProps) {
  const heights = getHeights(variant);
  const sources = getSources(variant, tone);

  if (variant === 'icon') {
    return (
      <img
        src={sources.icon}
        alt={alt}
        style={{
          display: 'block',
          height: heights.icon,
          width: heights.icon,
          objectFit: 'contain',
          ...style,
        }}
      />
    );
  }

  const classes = [
    'acc-brand-lockup',
    `acc-brand-lockup--${variant}`,
    `acc-brand-lockup--${tone}`,
  ];
  if (responsive) {
    classes.push('acc-brand-lockup--responsive');
  }

  return (
    <span className={classes.join(' ')} style={style}>
      <img
        className="acc-brand-lockup__full"
        src={sources.full}
        alt={alt}
        style={{ height: heights.full }}
      />
      {responsive && (
        <img
          className="acc-brand-lockup__icon"
          src={sources.icon}
          alt={alt}
          style={{ height: heights.icon, width: heights.icon }}
        />
      )}
    </span>
  );
}
